<template>
  <!-- Main Content -->
  <div>
    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 lg:mb-8 gap-4">
      <h1 class="page-title text-2xl lg:text-3xl">{{ $t('admin.overview.title') }}</h1>
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5BE32]"></div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="bg-red-500 text-white p-4 rounded-lg mb-6">
      {{ error }}
    </div>
    
          <!-- Content -->
    <div v-else>
      
      <!-- Summary Cards -->
      <div class="summary-cards grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
        <div class="summary-card">
          <div class="card-icon red">🪑</div>
          <div class="card-content">
            <h3 class="text-sm lg:text-base">{{ $t('admin.overview.summaryCards.occupancy') }}</h3>
            <p class="card-value text-lg lg:text-xl">{{ stats.occupancy.current }}/{{ stats.occupancy.total }}</p>
          </div>
        </div>
        
        <div class="summary-card">
          <div class="card-icon yellow">🔔</div>
          <div class="card-content">
            <h3 class="text-sm lg:text-base">{{ $t('admin.overview.summaryCards.activeDeliveryOrders') }}</h3>
            <p class="card-value text-lg lg:text-xl">{{ stats.activeDeliveryOrders }}</p>
          </div>
        </div>
        
        <div class="summary-card">
          <div class="card-icon blue">🔄</div>
          <div class="card-content">
            <h3 class="text-sm lg:text-base">{{ $t('admin.overview.summaryCards.inHousePreOrders') }}</h3>
            <p class="card-value text-lg lg:text-xl">{{ stats.inHousePreOrders }}</p>
          </div>
        </div>
        
        <div class="summary-card">
          <div class="card-icon purple">👤</div>
          <div class="card-content">
            <h3 class="text-sm lg:text-base">{{ $t('admin.overview.summaryCards.totalUsers') }}</h3>
            <p class="card-value text-lg lg:text-xl">{{ stats.totalUsers }}</p>
          </div>
        </div>
      </div>

    <!-- Main Dashboard Section -->
    <div class="dashboard-section space-y-6 lg:space-y-8">
      <!-- Orders Table -->
      <div class="orders-section">
        <h2 class="text-lg lg:text-xl font-semibold text-white mb-4">{{ $t('admin.overview.latestOrders') }}</h2>
        <div class="orders-table overflow-x-auto">
          <table class="w-full min-w-[600px]">
            <thead>
              <tr>
                <th class="text-left p-3 text-gray-300 font-semibold text-sm">{{ $t('admin.overview.tableHeaders.customer') }}</th>
                <th class="text-left p-3 text-gray-300 font-semibold text-sm">{{ $t('admin.overview.tableHeaders.amount') }}</th>
                <th class="text-left p-3 text-gray-300 font-semibold text-sm">{{ $t('admin.overview.tableHeaders.status') }}</th>
                <th class="text-left p-3 text-gray-300 font-semibold text-sm">{{ $t('admin.overview.tableHeaders.date') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in latestOrders" :key="order.id">
                <td class="p-3 text-white text-sm">{{ order.customerName || order.userEmail || 'N/A' }}</td>
                <td class="p-3 text-white text-sm">{{ order.formattedAmount }}</td>
                <td class="p-3">
                  <span 
                    :class="`inline-flex items-center gap-1 px-2 lg:px-4 py-1 ${order.displayStatus.bgColor} ${order.displayStatus.textColor} text-xs font-medium rounded-full whitespace-nowrap`"
                  >
                    <div :class="`w-2 h-2 ${order.displayStatus.dotColor} rounded-full`"></div>
                    {{ $t('admin.orders.statuses.' + order.status) }}
                  </span>
                </td>
                <td class="p-3 text-white text-sm">{{ order.formattedDate }}</td>
              </tr>
              <tr v-if="latestOrders.length === 0">
                <td colspan="4" class="text-center text-gray-400 py-4">{{ $t('admin.overview.noOrdersFound') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="charts-section grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        <!-- Sales Performance Chart -->
        <div class="chart-container">
          <div class="chart-header mb-4">
            <h2 class="text-lg lg:text-xl font-semibold text-white mb-3">{{ $t('admin.overview.salesPerformance') }}</h2>
            <div class="chart-filter flex flex-wrap gap-2 lg:gap-3">
              <span 
                :class="['filter-option text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 rounded cursor-pointer transition-colors whitespace-nowrap', currentPeriod === 'daily' ? 'active' : '']"
                @click="changePeriod('daily')"
              >
                {{ $t('admin.overview.daily') }}
              </span>
              <span 
                :class="['filter-option text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 rounded cursor-pointer transition-colors whitespace-nowrap', currentPeriod === 'weekly' ? 'active' : '']"
                @click="changePeriod('weekly')"
              >
                {{ $t('admin.overview.weekly') }}
              </span>
              <span 
                :class="['filter-option text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2 rounded cursor-pointer transition-colors whitespace-nowrap', currentPeriod === 'monthly' ? 'active' : '']"
                @click="changePeriod('monthly')"
              >
                {{ $t('admin.overview.monthly') }}
              </span>
            </div>
          </div>
          

          <div class="chart">
            <div v-if="Object.keys(salesData).length === 0" class="flex items-center justify-center h-full text-gray-400">
              <div class="text-center">
                <p class="text-lg mb-2">No sales data available</p>
                <p class="text-sm">Orders will appear here once they are placed</p>
              </div>
            </div>
            <div v-else class="chart-bars" :data-period="currentPeriod">
              <div 
                v-for="(value, key) in salesData" 
                :key="key"
                class="bar" 
                :style="{ height: getBarHeight(value) + '%' }"
              >
                <span class="bar-label">{{ key }}</span>
                <span class="bar-value">{{ formatCurrency(value) }}</span>
              </div>
            </div>
            <div class="chart-line"></div>
          </div>
        </div>

        <!-- Top Selling Items -->
        <div class="top-items">
          <h2 class="text-lg lg:text-xl font-semibold text-white mb-4">{{ $t('admin.overview.topSellingItems') }}</h2>
          <div class="items-list space-y-3">
            <div v-for="item in topItems" :key="item.name" class="item flex justify-between items-center p-3 bg-[#2A2A2A] rounded-lg">
              <div class="item-info flex items-center gap-3">
                <span class="item-name text-sm lg:text-base text-white font-medium">
                  {{ getTranslatedDishName(item.name) }}
                </span>
                <div :class="['item-trend', item.trend === 'up' ? 'green' : 'red']">
                  <svg width="30" height="15" viewBox="0 0 30 15">
                    <path 
                      :d="item.trend === 'up' ? 'M2 12 L8 8 L15 10 L22 6 L28 4' : 'M2 4 L8 8 L15 6 L22 10 L28 12'" 
                      :stroke="item.trend === 'up' ? '#4CAF50' : '#FF4444'" 
                      stroke-width="2" 
                      fill="none"
                    />
                  </svg>
                </div>
              </div>
              <div class="item-sales flex items-center gap-2">
                <span class="text-sm lg:text-base text-white font-semibold">{{ formatCurrency(item.totalSales) }}</span>
                <span :class="['trend-arrow text-lg', item.trend === 'up' ? 'green' : 'red']">
                  {{ item.trend === 'up' ? '↑' : '↓' }}
                </span>
              </div>
            </div>
            <div v-if="topItems.length === 0" class="text-center text-gray-400 py-4">
              {{ $t('admin.overview.noItemsFound') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import adminOverviewService from "../services/adminOverviewService";
import { adminOrderService } from "../services/adminOrderService";
import { getTranslatedDishName } from "@/utils/dishTranslation"; // <-- Import this

export default {
name: 'AdminOverview',
components: {
},
data() {
  return {
    loading: false,
    error: null,
    stats: {
      occupancy: { current: 0, total: 18 },
      activeDeliveryOrders: 0,
      inHousePreOrders: 0,
      totalUsers: 0,
      totalRevenue: 0,
      todayRevenue: 0,
      weeklyRevenue: 0,
      monthlyRevenue: 0
    },
    latestOrders: [],
    salesData: {},
    topItems: [],
    currentPeriod: 'daily'
  }
},
async mounted() {
  await this.loadOverviewData();
},
methods: {
  async loadOverviewData() {
    this.loading = true;
    this.error = null;
    
    try {
      // Load all data in parallel
      const [stats, ordersData, sales, items] = await Promise.all([
        adminOverviewService.getOverviewStats(),
        adminOrderService.fetchOrders({ limit: 10 }),
        adminOverviewService.getSalesPerformance(this.currentPeriod),
        adminOverviewService.getTopSellingItems(5)
      ]);
      
      console.log('Overview Stats:', stats);
      console.log('Orders Data:', ordersData);
      console.log('Sales Performance Data:', sales);
      console.log('Top Items:', items);
      
      this.stats = stats;
      this.latestOrders = ordersData.orders.map(order => adminOrderService.formatOrderForDisplay(order));
      this.salesData = sales;
      this.topItems = items;
      
    } catch (error) {
      this.error = error.message;
      console.error('Error loading overview data:', error);
    } finally {
      this.loading = false;
    }
  },

  async changePeriod(period) {
    this.currentPeriod = period;
    await this.loadSalesData();
  },

  async loadSalesData() {
    try {
      const salesData = await adminOverviewService.getSalesPerformance(this.currentPeriod);
      console.log('New Sales Data for period', this.currentPeriod, ':', salesData);
      this.salesData = salesData;
    } catch (error) {
      console.error('Error loading sales data:', error);
    }
  },



  formatCurrency(amount) {
    return adminOverviewService.formatCurrency(amount);
  },
  getTranslatedDishName(name) {
    return getTranslatedDishName(name, this.$t);
  },
  formatTime(date) {
    return adminOverviewService.formatTime(date);
  },

  formatDate(date) {
    return adminOverviewService.formatDate(date);
  },



  getBarHeight(value) {
    if (!this.salesData || Object.keys(this.salesData).length === 0) {
      return 0;
    }
    const maxValue = Math.max(...Object.values(this.salesData));
    if (maxValue <= 0) return 0;
    
    // Ensure minimum height for visibility
    const calculatedHeight = (value / maxValue) * 80; // Use 80% of chart height
    return Math.max(calculatedHeight, 5); // Minimum 5% height
  },

  getStatusClass(status) {
    const statusConfig = adminOverviewService.getStatusBadgeConfig();
    const config = statusConfig[status] || statusConfig.pending;
    return `status ${config.bgColor} ${config.textColor}`;
  },

  getStatusLabel(status) {
    const statusConfig = adminOverviewService.getStatusBadgeConfig();
    const config = statusConfig[status] || statusConfig.pending;
    return config.label;
  },

  handleLogout() {
    this.$router.push('/admin/login')
  }
}
}
</script> 

<style scoped>
.admin-dashboard {
display: flex;
min-height: 100vh;
background-color: #1F1F1F;
color: white;
}

/* Sidebar Styles */
.sidebar {
width: 250px;
background-color: #4F4F4F;
padding: 20px;
display: flex;
flex-direction: column;
border-right: 1px solid #1F1F1F;
}

.logo-section {
display: flex;
align-items: center;
margin-bottom: 40px;
}

.logo {
width: 60px;
height: 60px;
border-radius: 50%;
margin-right: 15px;
}

.logo-text {
font-weight: bold;
font-size: 16px;
color: #F5BE32;
}

.nav-menu {
flex: 1;
}

.nav-item {
display: flex;
align-items: center;
justify-content: flex-start;
padding: 16px 20px;
margin-bottom: 8px;
border-radius: 8px;
cursor: pointer;
transition: background-color 0.3s;
}

.nav-item:hover {
background-color: #1F1F1F;
}

.nav-item.active {
background-color: #F5BE32;
color: #1F1F1F;
}

.nav-item span {
font-size: 16px;
font-weight: 500;
}

.logout-section {
border-top: 1px solid #1F1F1F;
padding-top: 20px;
}

/* Main Content Styles */
.main-content {
flex: 1;
padding: 30px;
background-color: #1F1F1F;
}

.page-title {
font-size: 32px;
font-weight: bold;
color: #F5BE32;
margin-bottom: 30px;
}

/* Summary Cards */
.summary-cards {
display: grid;
grid-template-columns: repeat(4, 1fr);
gap: 20px;
margin-bottom: 30px;
}

.summary-card {
background-color: #4F4F4F;
padding: 20px;
border-radius: 12px;
display: flex;
align-items: center;
border: 1px solid #1F1F1F;
}

.card-icon {
width: 50px;
height: 50px;
border-radius: 12px;
display: flex;
align-items: center;
justify-content: center;
font-size: 24px;
margin-right: 15px;
}

.card-icon.red {
  background-color: #ff4444;
}
.card-icon.yellow {
  background-color: #f5be32;
}
.card-icon.blue {
  background-color: #4a90e2;
}
.card-icon.purple {
  background-color: #9b59b6;
}

.card-content h3 {
margin: 0 0 5px 0;
font-size: 14px;
color: #CCCCCC;
font-weight: normal;
}

.card-value {
margin: 0;
font-size: 24px;
font-weight: bold;
color: white;
}

/* Dashboard Section */
.dashboard-section {
display: grid;
grid-template-columns: 2fr 1fr;
gap: 30px;
}

/* Orders Section */
.orders-section {
background-color: #4F4F4F;
border-radius: 12px;
padding: 20px;
border: 1px solid #1F1F1F;
}

.orders-section h2 {
margin: 0 0 20px 0;
font-size: 20px;
color: white;
}

.orders-table {
overflow-x: auto;
}

table {
width: 100%;
border-collapse: collapse;
}

th, td {
padding: 8px 12px;
text-align: left;
border-bottom: 1px solid #1F1F1F;
}

th {
font-weight: 600;
color: #CCCCCC;
font-size: 13px;
}

td {
color: white;
font-size: 13px;
}

.status {
padding: 4px 12px;
border-radius: 16px;
font-size: 11px;
font-weight: 500;
white-space: nowrap;
display: inline-block;
min-width: fit-content;
}

.status.served {
background-color: #4CAF50;
color: white;
}

.status.in-progress {
background-color: #F5BE32;
color: #1F1F1F;
}



/* Charts Section */
.charts-section {
display: flex;
flex-direction: column;
gap: 20px;
}

/* Responsive chart improvements */
@media (max-width: 1200px) {
  .chart-bars {
    gap: 22px;
    padding: 20px 12px 60px 12px;
  }
  
  .bar {
    width: 45px;
    min-width: 40px;
    max-width: 55px;
  }
}

@media (max-width: 768px) {
  .chart-bars {
    gap: 18px;
    padding: 20px 10px 55px 10px;
  }
  
  .bar {
    width: 40px;
    min-width: 35px;
    max-width: 45px;
  }
  
  .bar-label {
    font-size: 11px;
    bottom: -40px;
  }
  
  .bar-value {
    font-size: 10px;
    top: -35px;
    padding: 3px 6px;
  }
}

@media (max-width: 480px) {
  .chart-bars {
    gap: 15px;
    padding: 20px 8px 50px 8px;
  }
  
  .bar {
    width: 35px;
    min-width: 30px;
    max-width: 40px;
  }
  
  .bar-label {
    font-size: 10px;
    bottom: -38px;
  }
  
  .bar-value {
    font-size: 9px;
    top: -32px;
    padding: 2px 5px;
  }
}

/* Chart period-specific styling for consistent bar widths */
.chart-bars[data-period="daily"] .bar {
  width: 45px;
  min-width: 40px;
  max-width: 50px;
}

.chart-bars[data-period="weekly"] .bar {
  width: 60px;
  min-width: 55px;
  max-width: 70px;
}

.chart-bars[data-period="monthly"] .bar {
  width: 50px;
  min-width: 45px;
  max-width: 55px;
}

/* Responsive adjustments for period-specific bar widths */
@media (max-width: 1200px) {
  .chart-bars[data-period="daily"] .bar {
    width: 40px;
    min-width: 35px;
    max-width: 45px;
  }
  
  .chart-bars[data-period="weekly"] .bar {
    width: 55px;
    min-width: 50px;
    max-width: 65px;
  }
  
  .chart-bars[data-period="monthly"] .bar {
    width: 45px;
    min-width: 40px;
    max-width: 50px;
  }
}

@media (max-width: 768px) {
  .chart-bars[data-period="daily"] .bar {
    width: 35px;
    min-width: 30px;
    max-width: 40px;
  }
  
  .chart-bars[data-period="weekly"] .bar {
    width: 50px;
    min-width: 45px;
    max-width: 60px;
  }
  
  .chart-bars[data-period="monthly"] .bar {
    width: 40px;
    min-width: 35px;
    max-width: 45px;
  }
}

@media (max-width: 480px) {
  .chart-bars[data-period="daily"] .bar {
    width: 30px;
    min-width: 25px;
    max-width: 35px;
  }
  
  .chart-bars[data-period="weekly"] .bar {
    width: 45px;
    min-width: 40px;
    max-width: 55px;
  }
  
  .chart-bars[data-period="monthly"] .bar {
    width: 35px;
    min-width: 30px;
    max-width: 40px;
  }
}

.chart-container {
background-color: #4F4F4F;
border-radius: 12px;
padding: 20px;
border: 1px solid #1F1F1F;
overflow: visible;
min-height: 400px;
display: flex;
flex-direction: column;
}

.chart-header {
display: flex;
justify-content: space-between;
align-items: center;
margin-bottom: 20px;
}

.chart-header h2 {
margin: 0;
font-size: 20px;
color: white;
}

.chart-filter {
display: flex;
gap: 10px;
}

.filter-option {
padding: 6px 12px;
border-radius: 6px;
font-size: 12px;
cursor: pointer;
color: #CCCCCC;
transition: all 0.2s ease;
border: 1px solid transparent;
}

.filter-option:hover {
background-color: rgba(245, 190, 50, 0.1);
color: #F5BE32;
border-color: rgba(245, 190, 50, 0.3);
}

.filter-option.active {
background-color: #F5BE32;
color: #1F1F1F;
border-color: #F5BE32;
font-weight: 500;
}

.chart {
height: 300px;
position: relative;
min-height: 300px;
overflow: visible;
flex: 1;
display: flex;
flex-direction: column;
}

.chart-bars {
display: flex;
align-items: end;
justify-content: center;
height: 200px;
padding: 20px 15px 60px 15px;
position: relative;
gap: 25px;
flex: 1;
}

.bar {
width: 50px;
min-width: 40px;
max-width: 70px;
background-color: #F5BE32;
border-radius: 6px;
position: relative;
display: flex;
flex-direction: column;
align-items: center;
justify-content: end;
min-height: 20px;
transition: all 0.2s ease;
}

.bar:hover {
background-color: #E6A800;
transform: scale(1.05);
}

.bar-label {
position: absolute;
bottom: -45px;
font-size: 12px;
color: #CCCCCC;
text-align: center;
width: 100%;
transform: translateX(-50%);
left: 50%;
white-space: nowrap;
font-weight: 500;
z-index: 10;
}

.bar-value {
position: absolute;
top: -40px;
font-size: 11px;
color: #CCCCCC;
white-space: nowrap;
font-weight: 500;
background-color: rgba(31, 31, 31, 0.95);
padding: 4px 8px;
border-radius: 4px;
z-index: 10;
}

.chart-line {
position: absolute;
top: 0;
left: 0;
right: 0;
bottom: 0;
pointer-events: none;
}

.chart-line::before {
display: none;
}

/* Top Items */
.top-items {
background-color: #4F4F4F;
border-radius: 12px;
padding: 20px;
border: 1px solid #1F1F1F;
}

.top-items h2 {
margin: 0 0 20px 0;
font-size: 20px;
color: white;
}

.items-list {
display: flex;
flex-direction: column;
gap: 15px;
}

.item {
display: flex;
justify-content: space-between;
align-items: center;
padding: 10px 0;
border-bottom: 1px solid #1F1F1F;
}

.item:last-child {
border-bottom: none;
}

.item-info {
display: flex;
align-items: center;
gap: 10px;
}

.item-name {
font-size: 14px;
color: white;
}

.item-trend {
display: flex;
align-items: center;
justify-content: center;
}

.item-trend svg {
display: block;
}

.item-sales {
display: flex;
align-items: center;
gap: 8px;
font-size: 14px;
color: #CCCCCC;
font-weight: 500;
}

.trend-arrow {
font-size: 12px;
font-weight: bold;
}

.trend-arrow.green {
color: #4CAF50;
}

.trend-arrow.red {
color: #FF4444;
}

/* Responsive Design */
@media (max-width: 1200px) {
.summary-cards {
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.dashboard-section {
  grid-template-columns: 1fr;
  gap: 20px;
}

.chart-container {
  padding: 15px;
}

.orders-section {
  padding: 15px;
}
}

@media (max-width: 768px) {
.admin-dashboard {
  flex-direction: column;
}

.sidebar {
  width: 100%;
  padding: 15px;
}

.main-content {
  padding: 15px;
}

.summary-cards {
  grid-template-columns: 1fr;
  gap: 15px;
}

.chart-container {
  padding: 12px;
}

.orders-section {
  padding: 12px;
}

.top-items {
  padding: 12px;
}
}

@media (max-width: 480px) {
.main-content {
  padding: 10px;
}

.summary-cards {
  gap: 10px;
}

.summary-card {
  padding: 15px;
}

.chart-container {
  padding: 10px;
}

.orders-section {
  padding: 10px;
}

.top-items {
  padding: 10px;
}

.chart-header {
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.chart-filter {
  flex-wrap: wrap;
  gap: 8px;
}
}
</style>
